import { queryProcedureResult } from 'iguazu-rpc';
import * as url from 'url';
import { Store } from 'redux';
import getOneIdentityUrl from '../selectors/getOneIdentityUrl';
import { ReadProfileResponse, RouterNextState, RouterReplace } from '../types';
import setAmexSessionTarget from './setAmexSessionTarget';
import { isAuthorizedForRoute } from './isAuthorizedForRoute';

const isAuthenticationTokenInvalidOrExpired = (response: ReadProfileResponse) =>
  response &&
  response.data &&
  'error' in response.data &&
  response.data.error &&
  (response.data.error.includes('invalid_authentication_token') ||
    response.data.error.includes('expired_authentication_token') ||
    response.data.error.includes('expired_session') ||
    response.data.error.includes('invalid_nonloggedin_token'));
const USER_NOT_LINKED_ERROR = 'User not linked to supplier dashboard';

const requireAuth = (store: Store) => {
  const { dispatch, getState } = store;

  // eslint-disable-next-line complexity -- this function is complex by nature
  return async (nextState: RouterNextState, replace: RouterReplace, callback?: () => void) => {
    const replaceServerOrClient = (location: string) => {
      if (location.includes('http')) {
        if (global.BROWSER) {
          window.location.replace(location);
        } else {
          replace({ state: url.parse(location) });
        }
      } else {
        replace(location);
      }
    };

    try {
      const origin = getState().getIn(['browser', 'location', 'origin']);
      const currentUri = `${origin}${nextState.location.pathname}`;
      setAmexSessionTarget(origin);
      const response = await dispatch(
        queryProcedureResult<object, ReadProfileResponse>({
          procedureName: 'readSupplierExperienceUserProfileV1',
          args: {},
        })
      ).promise;

      switch (response.status) {
        case 200:
          if (Object.keys(response.data).length === 0) {
            replaceServerOrClient(`${nextState.location.pathname}/access-error`);
          }

          // redirect to unauth page for deactivated IM only user
          if (
            'productTypes' in response.data &&
            response.data.productTypes.length === 1 &&
            response.data.productTypes[0] === 'INVOICE_MANAGEMENT' &&
            !response.data.invoiceManagementRole
          ) {
            replaceServerOrClient(
              `/en-us/business/payment-solutions/supplier-dashboard/unauthorized-error`
            );
            break;
          }

          if (
            nextState.location.pathname.includes('view-payment') &&
            nextState.location.query?.id &&
            nextState.location.query?.signature
          ) {
            if (
              'productTypes' in response.data &&
              response.data.productTypes.length === 1 &&
              response.data.productTypes[0] === 'INVOICE_MANAGEMENT'
            ) {
              replaceServerOrClient(
                `/en-us/business/payment-solutions/supplier-dashboard/link-account${nextState.location.search}`
              );
              break;
            }

            const { dataId, sourceSystem } = nextState.location.query;
            replaceServerOrClient(
              `${nextState.location.pathname}?dataId=${dataId}${
                sourceSystem ? `&sourceSystem=${sourceSystem}` : ''
              }`
            );
          }
          if (nextState.location.pathname.includes('home') && nextState.location?.search) {
            replaceServerOrClient(`${nextState.location.pathname}`);
          }
          if ('productTypes' in response.data && response.data.productTypes.length === 0) {
            replaceServerOrClient(`${nextState.location.pathname}/no-products`);
          }
          if (!isAuthorizedForRoute(nextState, response)) {
            replaceServerOrClient(
              `/en-us/business/payment-solutions/supplier-dashboard/unauthorized-error`
            );
          }
          break;
        case 401:
          if (isAuthenticationTokenInvalidOrExpired(response)) {
            const destURL = `${currentUri}${nextState.location.search}`;
            replaceServerOrClient(
              `${getOneIdentityUrl(getState())}?DestPage=${encodeURIComponent(destURL)}`
            );
          } else {
            replaceServerOrClient(`${nextState.location.pathname}/unauthorized-error`);
          }
          break;
        case 404: {
          const errorMessage = 'error' in response.data ? response.data.error : undefined;
          if (errorMessage && errorMessage.includes(USER_NOT_LINKED_ERROR)) {
            if (nextState?.location?.query?.relogin) {
              const destURL = `${currentUri}?id=${nextState.location.query?.id}&signature=${nextState.location.query?.signature}&dataId=${nextState.location.query?.dataId}`;
              replaceServerOrClient(
                `${getOneIdentityUrl(getState())}?DestPage=${encodeURIComponent(destURL)}`
              );
              break;
            }
            if (nextState.location.pathname.includes('link-account')) {
              break;
            } else {
              const linkAccountRoute = `/en-us/business/payment-solutions/supplier-dashboard/link-account${nextState.location.search}`;
              replaceServerOrClient(linkAccountRoute);
            }
            break;
          } else {
            replaceServerOrClient(`${nextState.location.pathname}system-error`);
          }
          break;
        }
        default:
          replaceServerOrClient(`${nextState.location.pathname}/system-error`);
          break;
      }
    } catch (error) {
      replaceServerOrClient(`${nextState.location.pathname}/system-error`);
    }
    if (callback) {
      callback();
    }
  };
};

export default requireAuth;
